// src/pages/TimeAudit.jsx
import WorkspaceInsights from '../components/brain/WorkspaceInsights';
import React, { useState, useEffect, useCallback } from "react";
import { Card } from "components/ui/card";
import { Button } from "components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "components/ui/dialog";
import { PageHeader, StatGrid, Stat } from "components/ui/page-header";
import { Collapsible } from "components/ui/compact";
import {
  Clock,
  AlertTriangle,
  Brain,
  MessageSquare,
  BarChart3,
  CheckCircle,
  Calendar as CalendarIcon,
  Lightbulb,
  Plus
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { timeEntriesService, timeReflectionsService } from "../services/database";
import CalendarImportDialog from "../components/timeaudit/CalendarImportDialog";
import FocusPlanner from "../components/timeaudit/FocusPlanner";
import { localDateString } from "../lib/calendarDates";

// Mock calendar data generator
const getActivityCategory = (activity) => {
  if (activity.includes('Research') || activity.includes('Customer') || activity.includes('User')) return 'Discovery';
  if (activity.includes('Planning') || activity.includes('Product') || activity.includes('Architecture')) return 'Strategy';
  if (activity.includes('Review') || activity.includes('Standup') || activity.includes('Triage')) return 'Execution';
  if (activity.includes('Sync') || activity.includes('1:1') || activity.includes('All Hands')) return 'Communication';
  return 'Administrative';
};

const getCategoryColor = (category) => {
  const colors = {
    Discovery: 'var(--success)',
    Strategy: 'var(--accent)',
    Execution: 'var(--warning)',
    Communication: 'var(--info)',
    Administrative: 'var(--fg-subtle)'
  };
  return colors[category] || 'var(--fg-muted)';
};

// Transform database time entries into week view format
const transformTimeEntriesToWeekData = (timeEntries, startOfWeek) => {
  const weekData = [];

  // Create 7 days starting from startOfWeek
  for (let i = 0; i < 7; i++) {
    const date = new Date(startOfWeek);
    date.setDate(startOfWeek.getDate() + i);
    const dateString = localDateString(date);

    // Find activities for this date
    const dayActivities = timeEntries
      .filter(entry => entry.date === dateString && !['scheduled', 'cancelled'].includes(entry.planning_status))
      .map(entry => ({
        id: entry.id,
        title: entry.activity_title,
        duration: entry.duration_minutes || 30,
        strategicValue: entry.strategic_value || 'Medium',
        category: entry.category || getActivityCategory(entry.activity_title)
      }));

    weekData.push({
      date: date.toDateString(),
      dayName: date.toLocaleDateString('en', { weekday: 'short' }),
      activities: dayActivities
    });
  }

  return weekData;
};

export default function TimeAudit() {
  const { user, loading } = useAuth();
  const [weekData, setWeekData] = useState([]);
  const [reflections, setReflections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [newReflection, setNewReflection] = useState('');
  const [showReflectionForm, setShowReflectionForm] = useState(false);
  const [showTimeEntryForm, setShowTimeEntryForm] = useState(false);
  const [newTimeEntry, setNewTimeEntry] = useState({
    title: '',
    duration: 30,
    category: 'Execution',
    strategicValue: 'Medium',
    date: localDateString(new Date())
  });

  // Load data on mount
  const loadData = useCallback(async ({ silent = false } = {}) => {
    if (loading || !user?.id) {
      if (!loading && !user?.id) {
        setWeekData([]);
        setReflections([]);
        setIsLoading(false);
      }
      return;
    }

    if (!silent) setIsLoading(true);
    setLoadError("");
    try {
      console.log('TimeAudit loading data for user:', user.id);

      // Get current week date range (but also fetch a wider range to show all entries)
      const now = new Date();
      const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));

      // Extend range: 4 weeks in the past, 8 weeks in the future
      const extendedStart = new Date(startOfWeek);
      extendedStart.setDate(startOfWeek.getDate() - 28); // 4 weeks back

      const extendedEnd = new Date(startOfWeek);
      extendedEnd.setDate(startOfWeek.getDate() + 56); // 8 weeks forward

      const startDateStr = localDateString(extendedStart);
      const endDateStr = localDateString(extendedEnd);

      console.log('📅 Searching for entries in date range:', { startDateStr, endDateStr });

      // Load real data from database
      const [timeEntries, userReflections] = await Promise.all([
        timeEntriesService.getWeekData(startDateStr, endDateStr),
        timeReflectionsService.list("-created_at", 10) // Get recent reflections
      ]);

      console.log('TimeAudit data loaded:', {
        timeEntries: timeEntries.length,
        reflections: userReflections.length
      });

      // Transform time entries into week format
      const weekData = transformTimeEntriesToWeekData(timeEntries, startOfWeek);

      setWeekData(weekData);
      setReflections(userReflections);

    } catch (error) {
      console.error('Error loading TimeAudit data:', error);
      setLoadError('Could not load your time data. Please try again.');
      // Keep the view empty on errors; never substitute synthetic activity.
      const weekData = [];
      setWeekData(weekData);
      setReflections([]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, loading]);

  // Calculate current week start for calendar import
  const currentWeekStart = React.useMemo(() => {
    const now = new Date();
    return new Date(now.setDate(now.getDate() - now.getDay()));
  }, []);

  // Format week date range for display
  const weekDateRange = React.useMemo(() => {
    const start = new Date(currentWeekStart);
    const end = new Date(currentWeekStart);
    end.setDate(start.getDate() + 6);

    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    return `${formatDate(start)} - ${formatDate(end)}`;
  }, [currentWeekStart]);

  useEffect(() => {
    loadData();
    window.addEventListener('spcc:autopilot-updated', loadData);
    return () => window.removeEventListener('spcc:autopilot-updated', loadData);
  }, [loadData]);

  // Calculate time metrics
  const timeMetrics = React.useMemo(() => {
    if (!weekData.length) return {};

    let totalTime = 0;
    const categoryTime = {};
    const strategicTime = { High: 0, Medium: 0, Low: 0 };

    weekData.forEach(day => {
      day.activities.forEach(activity => {
        totalTime += activity.duration;
        categoryTime[activity.category] = (categoryTime[activity.category] || 0) + activity.duration;
        strategicTime[activity.strategicValue] += activity.duration;
      });
    });

    const strategicPercentage = totalTime ? Math.round((strategicTime.High / totalTime) * 100) : 0;
    const avgDailyTime = Math.round(totalTime / 7);

    return {
      totalTime,
      categoryTime,
      strategicTime,
      strategicPercentage,
      avgDailyTime,
      topCategory: Object.entries(categoryTime).sort(([,a], [,b]) => b - a)[0]?.[0] || 'Unknown'
    };
  }, [weekData]);

  const handleAddReflection = async () => {
    if (!newReflection.trim()) return;

    try {
      const reflection = await timeReflectionsService.create({
        content: newReflection,
        week_identifier: 'current',
        reflection_type: 'weekly'
      });

      // Add to front of list
      setReflections([reflection, ...reflections]);
      setNewReflection('');
      setShowReflectionForm(false);
    } catch (error) {
      console.error('Error saving reflection:', error);
      alert('Failed to save reflection. Please try again.');
    }
  };

  const handleAddTimeEntry = async () => {
    if (!newTimeEntry.title.trim()) return;

    try {
      await timeEntriesService.create({
        activity_title: newTimeEntry.title,
        duration_minutes: newTimeEntry.duration,
        category: newTimeEntry.category,
        strategic_value: newTimeEntry.strategicValue,
        date: newTimeEntry.date
      });

      // Reload data to show the new entry
      await loadData();
      setShowTimeEntryForm(false);
      setNewTimeEntry({
        title: '',
        duration: 30,
        category: 'Execution',
        strategicValue: 'Medium',
        date: localDateString(new Date())
      });
    } catch (error) {
      console.error('Error saving time entry:', error);
      alert('Failed to save time entry. Please try again.');
    }
  };

  const handleCalendarImportComplete = async (importedCount) => {
    console.log(`✅ Calendar import complete: ${importedCount} entries imported`);
    console.log('🔄 Reloading TimeAudit data...');

    // Reload the week data to show the imported entries
    await loadData();

    console.log('✅ TimeAudit data reloaded');
  };

  // GPT-style strategic prompts
  const strategicPrompts = [
    "What strategic work did you ignore this week?",
    "Which meetings could have been emails?",
    "How much time did you spend on discovery vs. delivery?",
    "What would you do differently if you had 20% less time?",
    "Which activities moved you closer to your OKRs?"
  ];

  if (isLoading) {
    return (
      <div className="loading-screen" style={{ height: 240 }} role="status">
        <div><div className="spinner" /><span>Loading time audit…</span></div>
      </div>
    );
  }

  const hours = (m) => `${Math.floor((m || 0) / 60)}h ${(m || 0) % 60}m`;
  const categoryEntries = Object.entries(timeMetrics.categoryTime || {}).filter(([, minutes]) => minutes > 0);
  const resetEntryForm = () => {
    setShowTimeEntryForm(false);
    setNewTimeEntry({
      title: '',
      duration: 30,
      category: 'Execution',
      strategicValue: 'Medium',
      date: localDateString(new Date())
    });
  };

  return (
    <div>
      {loadError && <p role="alert" className="v2-error">{loadError}</p>}
      <PageHeader
        icon={Clock}
        eyebrow="Operate"
        title="Time Audit"
        description="Track where your time goes and what creates strategic value."
        actions={<>
          <CalendarImportDialog
            key={user?.id}
            onImportComplete={handleCalendarImportComplete}
            currentWeekStart={currentWeekStart}
          />
          <Button variant="primary" onClick={() => setShowTimeEntryForm(true)}>
            <Plus size={15} />
            Track time
          </Button>
        </>}
      />

      <StatGrid>
        <Stat label="Weekly total" value={hours(timeMetrics.totalTime)} hint={`${weekDateRange} · avg ${hours(timeMetrics.avgDailyTime)}/day`} />
        <Stat
          label="Strategic focus"
          value={`${timeMetrics.strategicPercentage}%`}
          hint="High-value activities"
          tone={timeMetrics.totalTime ? (timeMetrics.strategicPercentage > 60 ? 'success' : timeMetrics.strategicPercentage > 30 ? 'warning' : 'danger') : undefined}
        />
        <Stat
          label="Top category"
          value={timeMetrics.totalTime ? timeMetrics.topCategory : '—'}
          hint={`${Math.round((timeMetrics.categoryTime[timeMetrics.topCategory] || 0) / 60)}h this week`}
        />
      </StatGrid>
      <WorkspaceInsights area="time"/>
      <FocusPlanner onChange={() => loadData({ silent: true })}/>

      <div className="grid cols-2" style={{ alignItems: 'start' }}>
        <div style={{ display: 'grid', gap: 12 }}>
          <Card>
            <div className="card-header">
              <h2 className="card-title"><BarChart3 />Time by category</h2>
            </div>
            {categoryEntries.length === 0 ? (
              <div className="empty" style={{ padding: '20px 8px' }}>
                <span>No time tracked this week. Track time or import from your calendar.</span>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', height: 8, borderRadius: 999, overflow: 'hidden', background: 'var(--bg-muted)', gap: 2 }} role="img" aria-label="Share of time by category">
                  {categoryEntries.map(([category, minutes]) => (
                    <div key={category} style={{ width: `${(minutes / timeMetrics.totalTime) * 100}%`, background: getCategoryColor(category) }} />
                  ))}
                </div>
                <ul style={{ listStyle: 'none', margin: '12px 0 0', padding: 0, display: 'grid', gap: 6 }}>
                  {categoryEntries.map(([category, minutes]) => (
                    <li key={category} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: getCategoryColor(category) }} aria-hidden="true" />
                      <span style={{ fontWeight: 500 }}>{category}</span>
                      <span className="tabular" style={{ marginLeft: 'auto', color: 'var(--fg-muted)' }}>
                        {Math.round((minutes / timeMetrics.totalTime) * 100)}% · {hours(minutes)}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Card>

          <Collapsible id="time:focus-guard" icon={CalendarIcon} title="Focus guard" summary="Batch meetings · Block focus time · Keep discovery cadence">
            <div style={{ display: 'grid', gap: 8 }}>
              {[
                { icon: AlertTriangle, tone: 'warning', title: 'Too many meetings', body: 'Consider batching similar meetings or making some async.' },
                { icon: Lightbulb, tone: 'accent', title: 'Block focus time', body: 'Schedule 2-hour blocks for deep strategic work.' },
                { icon: CheckCircle, tone: 'success', title: 'Good discovery balance', body: 'Keep up the customer research cadence.' },
              ].map(({ icon: Icon, tone, title, body }) => (
                <div key={title} style={{ display: 'flex', gap: 10, padding: '10px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-inset)' }}>
                  <Icon size={15} color={`var(--${tone === 'accent' ? 'accent' : tone})`} style={{ marginTop: 2 }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{title}</div>
                    <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{body}</div>
                  </div>
                </div>
              ))}
            </div>
          </Collapsible>
        </div>

        <Card>
          <div className="card-header">
            <h2 className="card-title"><Brain />Weekly reflection</h2>
          </div>
          <Collapsible id="time:prompts" className="plain" title={`Reflection prompts · ${strategicPrompts.length}`} summary={strategicPrompts[0]}>
            <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 2, counterReset: 'prompt' }}>
              {strategicPrompts.map((prompt, index) => (
                <li key={index} style={{ display: 'flex', gap: 10, padding: '7px 0', fontSize: 13, color: 'var(--fg-secondary)', borderTop: index ? '1px solid var(--border)' : 0 }}>
                  <span className="tabular" style={{ color: 'var(--fg-subtle)', fontSize: 12, minWidth: 16 }}>{index + 1}</span>
                  {prompt}
                </li>
              ))}
            </ol>
          </Collapsible>

          {!showReflectionForm ? (
            <Button variant="secondary" onClick={() => setShowReflectionForm(true)} style={{ marginTop: 12 }}>
              <MessageSquare size={15} />
              Add weekly reflection
            </Button>
          ) : (
            <div style={{ marginTop: 12 }}>
              <textarea
                aria-label="Weekly reflection"
                value={newReflection}
                onChange={(e) => setNewReflection(e.target.value)}
                placeholder="Reflect on your week… What strategic work did you ignore? What created the most value?"
                style={{ minHeight: 110 }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
                <Button variant="ghost" onClick={() => { setShowReflectionForm(false); setNewReflection(''); }}>Cancel</Button>
                <Button variant="primary" onClick={handleAddReflection}>Save reflection</Button>
              </div>
            </div>
          )}

          {reflections.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h3 style={{ fontSize: 12, fontWeight: 500, margin: '0 0 6px', color: 'var(--fg-muted)' }}>Past reflections</h3>
              <div style={{ display: 'grid', gap: 6 }}>
                {reflections.slice(0, 3).map((reflection) => (
                  <div key={reflection.id} style={{ padding: '10px 12px', background: 'var(--bg-inset)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 13, color: 'var(--fg-secondary)', lineHeight: 1.5 }}>{reflection.content}</div>
                    <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 4 }}>
                      {new Date(reflection.created_at || reflection.timestamp).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>

      <Dialog open={showTimeEntryForm} onOpenChange={(open) => { if (!open) resetEntryForm(); }}>
        <DialogContent style={{ width: 480 }}>
          <DialogHeader>
            <DialogTitle>Track time</DialogTitle>
            <DialogDescription>Log an activity to see where your week goes.</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <label className="label" htmlFor="te-title">Activity *</label>
              <input
                id="te-title"
                type="text"
                value={newTimeEntry.title}
                onChange={(e) => setNewTimeEntry({ ...newTimeEntry, title: e.target.value })}
                placeholder="e.g., Sprint planning, customer call"
              />
            </div>
            <div className="grid cols-2">
              <div>
                <label className="label" htmlFor="te-duration">Duration (minutes)</label>
                <input
                  id="te-duration"
                  type="number"
                  value={newTimeEntry.duration}
                  onChange={(e) => setNewTimeEntry({ ...newTimeEntry, duration: parseInt(e.target.value) })}
                  min="5"
                  max="480"
                />
              </div>
              <div>
                <label className="label" htmlFor="te-date">Date</label>
                <input
                  id="te-date"
                  type="date"
                  value={newTimeEntry.date}
                  onChange={(e) => setNewTimeEntry({ ...newTimeEntry, date: e.target.value })}
                />
              </div>
              <div>
                <label className="label" htmlFor="te-category">Category</label>
                <select
                  id="te-category"
                  value={newTimeEntry.category}
                  onChange={(e) => setNewTimeEntry({ ...newTimeEntry, category: e.target.value })}
                >
                  <option value="Discovery">Discovery</option>
                  <option value="Strategy">Strategy</option>
                  <option value="Execution">Execution</option>
                  <option value="Communication">Communication</option>
                  <option value="Administrative">Administrative</option>
                </select>
              </div>
              <div>
                <label className="label" htmlFor="te-value">Strategic value</label>
                <select
                  id="te-value"
                  value={newTimeEntry.strategicValue}
                  onChange={(e) => setNewTimeEntry({ ...newTimeEntry, strategicValue: e.target.value })}
                >
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={resetEntryForm}>Cancel</Button>
            <Button variant="primary" onClick={handleAddTimeEntry} disabled={!newTimeEntry.title.trim()}>Save entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
